import React, { useEffect, useRef, useContext, createContext } from 'react'
import { useHistory } from 'react-router-dom'
import { useSelector, shallowEqual, useDispatch } from 'react-redux'
import { createSelector } from 'reselect'

import { Drawer, useTheme, makeStyles } from '@material-ui/core'

import useViewportSize from '~/components/useViewportSize'
import LoadingEcommerce from './LoadingEcommerce'
import RenderEcommerce from './RenderEcommerce'
import EcommerceNotFound from './EcommerceNotFound'
import { useDataEcommerce } from './useDataEcommerce'

import { selectChart } from '~/selectors'
import actions from '~/actions'
import api from '~/api'

const selector = createSelector(
  (state) => state.app,
  (...args) => args,
)

const useStyles = makeStyles(() => ({
  drawerMobile: {
    width: '100%',
    overflow: 'hidden',
    flexShrink: 0,
  },
  drawerDesktop: {
    width: 700,
    overflow: 'hidden',
    flexShrink: 0,
  },
}))

export const EcommerceContext = createContext()

export default function DrawerEcommerce({ openEcommerce, setOpenEcommerce, dataEcommerce, loadingEcommerce, config }) {
  const [app] = useSelector(selector, shallowEqual)
  const [chart] = useSelector(selectChart, shallowEqual)

  const ecommerce = useDataEcommerce(dataEcommerce, config)

  const mounted = useRef(false)

  const classes = useStyles()
  const theme = useTheme()
  const history = useHistory()
  const viewport = useViewportSize()
  const dispatch = useDispatch()
  const isMobile = viewport.width < theme.gofind.desktopMaxWidth
  const url = window.location.toString()

  const { currentProduct, stores, view } = app
  const { period } = chart

  const handleClose = () => {
    const isUrlRegex = url.match(/produto(?:([\/\w\-]+))[\d]+/g) || []

    dispatch(actions.dialog.error(false))
    dispatch(actions.dialog.reset())
    dispatch(actions.chart.reset())

    setOpenEcommerce(null)

    if (config.showContextViewStore) {
      if (currentProduct?.gtin !== 'all' || isUrlRegex.length) return

      history.push('/produto/todos')
      return
    }

    if (view === 'VIEW_STORES' && (!stores || !stores.length)) history.push('/')
  }

  useEffect(() => {
    mounted.current = true
    return () => (mounted.current = false)
  }, [])

  useEffect(() => {
    ;(async () => {
      if (!mounted.current || !ecommerce?.isEnableAlertPrice || !openEcommerce || !ecommerce?.priceMonitoring.length)
        return

      const isProductHavePriceMonitored = ecommerce?.priceMonitoring.filter(
        (pm) => pm.gtin === openEcommerce.gtin,
      ).length

      if (!isProductHavePriceMonitored) return

      try {
        dispatch(actions.chart.load(true))
        const insights = await api.ecommerceHistoryInsights(openEcommerce.gtin, period)

        if (Array.isArray(insights)) {
          dispatch(actions.chart.data(insights))
          return
        }

        dispatch(actions.chart.data([]))
      } catch (err) {
        console.error('TCL: ~ getHistoryInsights ~ err', err)
      } finally {
        dispatch(actions.chart.load(false))
      }
    })()
  }, [openEcommerce, period, ecommerce?.isEnableAlertPrice, ecommerce?.priceMonitoring])

  return (
    <>
      <LoadingEcommerce isLoading={loadingEcommerce} />

      {ecommerce?.isEcommerceNotFound && openEcommerce && !loadingEcommerce && (
        <EcommerceNotFound product={openEcommerce} onClose={handleClose} />
      )}

      <EcommerceContext.Provider value={{ ...ecommerce }}>
        <Drawer
          anchor="right"
          open={Boolean(!ecommerce?.isEcommerceNotFound) && Boolean(openEcommerce) && Boolean(!loadingEcommerce)}
          onClose={handleClose}
          classes={{ paper: isMobile ? classes.drawerMobile : classes.drawerDesktop }}
          PaperProps={{ 'data-cy': 'drawer-ecommerce' }}
        >
          <RenderEcommerce
            openEcommerce={openEcommerce}
            loadingEcommerce={loadingEcommerce}
            dataEcommerce={dataEcommerce}
            handleClose={handleClose}
            configEcommerce={config}
          />
        </Drawer>
      </EcommerceContext.Provider>
    </>
  )
}
